import {DialogTypes} from 'common/names';
import {pageSelectors} from 'features/layout/CommonPage/redux';

import {ICloseSubDialogAction} from 'features/layout/redux/actions';
import {isEmpty} from 'lodash';
import {call, SagaReturnType, select} from 'redux-saga/effects';

import i18n from 'i18n';
import {asyncSagaConstructor, unhandedSagaError} from 'redux/common';
import api from 'api';

import ApiService from 'api/ApiService';
import {CancelableConfirmationDialogResult} from 'components/dialogs/CancelableConfirmationDialog';
import {openDialogWithResult} from 'redux/common/saga/openDialogWithResult';
import {PendingChangesDialogData} from 'features/layout/DialogRoutes/dialogs/PendingChangesDialog';
import {loginSelectors, loginTypes} from '..';
import history from 'redux/history';
import {transactionsTypes} from 'redux/transactions';
import {ITroubleShootingForm} from 'features/layout/DialogRoutes/dialogs/TroubleShootingDialog';
import {BackupSettings} from 'types/BackupSettings';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {settingsTypes} from 'redux/settings';
import {licenseSelectors} from 'redux/license';
import {AdminResourceType} from 'types/diplomat/settings/adminroles/AdminResourceType';

export function* logoutSaga() {
   try {
      const isFeatureSupported = (yield select(licenseSelectors.isFeatureSupported)) as ReturnType<
         typeof licenseSelectors.isFeatureSupported
      >;
      const isReviewer = (yield select(loginSelectors.isReviewer)) as ReturnType<
         typeof loginSelectors.isReviewer
      >;

      if (isReviewer) {
         return (yield call(disconnect)) as SagaReturnType<typeof disconnect>;
      }

      // Persist changes for transaction and other forms
      // yield put(pageActions.persistDirtyValues());

      //if there are changes pending, find out if they want to continue
      //and lose the changes or quit
      const isPendingChanges = (yield select(pageSelectors.isPendingChanges)) as ReturnType<
         typeof pageSelectors.isPendingChanges
      >;
      if (isPendingChanges) {
         const result: ICloseSubDialogAction<boolean> = yield call(openDialogWithResult, {
            id: DialogTypes.PendingChangesDialog.id,
            data: {
               processLabel: i18n.t('exit'),
               confirmLabel: i18n.t('Exit'),
            } as PendingChangesDialogData,
         });
         if (result.payload?.data) {
            // lose changes
            // const form = (yield select(
            //    pageSelectors.getCurrentForm
            // )) as FormikProps<IDiplomatObject>;
            // form?.resetForm({
            //    values: form?.initialValues,
            //    touched: {},
            //    // dirty: false,
            // });
            // yield put(action(pageTypes.RESET_ALL_FORMS));
         } else {
            //quit
            return;
         }
      }

      const resources = (yield select(loginSelectors.getResourcesMap)) as ReturnType<
         typeof loginSelectors.getResourcesMap
      >;
      if (!resources[AdminResourceType.enum.SETTINGS]) {
         return (yield call(disconnect)) as SagaReturnType<typeof disconnect>; // Skip all settings related checks;
      }

      const troubleshooters = (yield call(
         asyncSagaConstructor(
            api.transactionsTroubleshooting,
            transactionsTypes.GET_TROUBLESHOOTING
         )
      )) as SagaReturnType<typeof asyncSagaConstructor>;
      if (!isEmpty(troubleshooters)) {
         const result: ICloseSubDialogAction<ITroubleShootingForm> = yield call(
            openDialogWithResult,
            {
               id: DialogTypes.TroubleShootingDialog.id,
               data: {
                  troubleshooters,
               } as PendingChangesDialogData,
            }
         );

         if (!result.payload?.data) {
            return;
         } else if (result.payload?.data.turnOffTroubleshooting) {
            yield call(
               asyncSagaConstructor(
                  api.turnOffTroubleshooting,
                  transactionsTypes.TURN_OFF_TROUBLESHOOTING,
                  {
                     getMethod: ApiService.postSecureJson,
                  }
               )
            );
         }
      }

      const backupSettings: BackupSettings = yield call(
         asyncSagaConstructor(api.backupSettings, settingsTypes.GET_BACKUP_SETTINGS)
      );
      let shouldBackup = CancelableConfirmationDialogResult.NO;
      if (
         isFeatureSupported(DiplomatFeature.BACKUP_RESTORE) &&
         (!backupSettings || backupSettings.backupOnExit)
      ) {
         const dialogResult: ICloseSubDialogAction<CancelableConfirmationDialogResult> = yield call(
            openDialogWithResult,
            {
               id: DialogTypes.CancelableConfirmationDialog.id,
               data: {
                  title: i18n.t('Transaction Datastore Backup'),
                  content: i18n.t('Do you want to backup the Transaction Datastore?'),
               },
            }
         );
         shouldBackup = dialogResult.payload!.data!;

         if (shouldBackup === CancelableConfirmationDialogResult.OK) {
            yield call(openDialogWithResult, {
               id: DialogTypes.BackupDialog.id,
            });
         }
      }

      if (shouldBackup !== CancelableConfirmationDialogResult.CANCEL) {
         (yield call(disconnect)) as SagaReturnType<typeof disconnect>;
      }
   } catch (e) {
      (yield call(unhandedSagaError, e as Error)) as SagaReturnType<typeof unhandedSagaError>;
   }
}

function* disconnect() {
   yield call(
      asyncSagaConstructor(api.disconnect, loginTypes.LOGOUT, {
         getMethod: ApiService.postSecureJson,
         onSuccess: () => {
            return history.push('/login');
         },
      })
   );
}
